import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "./LoadingSkeleton";
import EventBlock, {
  AllDayChip,
  gridHour,
  hm,
} from "./calendar/EventBlock";
import WeekCalendar from "./WeekCalendar";
import {
  IconChevronLeft,
  IconChevronRight,
  IconExpand,
} from "./icons";
import { describeError } from "../hooks/useApi";
import { useAuth } from "../hooks/useAuth";
import { getCalendarDay } from "../lib/api";
import { dayLabel, layoutDay, nowMinutes } from "../lib/agenda";
import { getCachedDay, setCachedDay, subscribeDayCache } from "../lib/dayCache";
import { shiftIsoDay } from "../lib/day";
import type { CalendarDay, CalendarEvent } from "../types";

/**
 * «Мій день» — the vertical hour grid (Vitalik, 2026-09-07).
 *
 * It replaced a single horizontal line squeezed into 100px of card. The line
 * could say *that* something was at 15:00; it could not say a meeting runs an
 * hour and the next one starts inside it, which is the thing a day is read for.
 * A day is a column of hours in every calendar he has ever used, so this is a
 * column of hours: true scale, overlapping events side by side, and the «зараз»
 * line cutting across at the real minute.
 *
 * Today's events come from `/api/summary` — the page already has them, and
 * asking again would paint the same day twice. Any other day is one
 * `GET /api/calendar/day` behind the `‹ ›`, kept in `lib/dayCache` so walking
 * back and forth costs nothing after the first look.
 *
 * The grid is drawn even when there is nothing on it. An empty day is a fact
 * about the day, not about the widget, and a blank card hides which fact it is
 * — «день вільний» and «немає доступу до календаря» look identical without it.
 *
 * One day is still one day. «На весь екран» opens the same grid for the whole
 * week ({@link WeekCalendar}) without moving the card off the day it is on.
 */

/**
 * One hour of grid. 14 default hours fit a desktop card at this height.
 *
 * Exported because `OverviewSkeleton` has to reserve exactly this card's
 * height before the data lands — a guessed placeholder is a guaranteed jump.
 */
export const HOUR_PX = 40;

interface DayTimelineProps {
  className?: string;
  /** The summary's own today (Kyiv) — the anchor every label is relative to. */
  today: string;
  /** False when Google is unconfigured or unauthorized, for `today` only. */
  available: boolean;
  /** `context.calendar_today` — the day the page already carries. */
  events: CalendarEvent[];
  /** Why today's calendar is empty, when it is not simply free. */
  note: string | null;
}

export default function DayTimeline({
  className,
  today,
  available,
  events,
  note,
}: DayTimelineProps) {
  // `null` means "whatever today is" rather than a date, so the card follows
  // midnight instead of pinning itself to the day it was opened on.
  const [pinned, setPinned] = useState<string | null>(null);
  const day = pinned ?? today;
  const isToday = day === today;
  // Whose cached days these are. `""` cannot collide with a real uuid, and a
  // card rendered without a session has nothing worth keeping anyway.
  const userId = useAuth().user?.id ?? "";

  const [remote, setRemote] = useState<CalendarDay | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Bumped by «Спробувати ще раз», and by «Оновити» emptying the day cache. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (day === today) {
      setRemote(null);
      setLoading(false);
      setError(null);
      return;
    }
    const cached = getCachedDay(userId, day);
    if (cached && !cached.stale) {
      setRemote(cached.value);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    // A stale day is drawn at once and re-asked behind the render; a day with
    // nothing cached is the only one that has to wait, and says so.
    setRemote(cached?.value ?? null);
    setError(null);
    setLoading(!cached);
    getCalendarDay(day)
      .then((answer) => {
        setCachedDay(userId, day, answer);
        if (cancelled) return;
        setRemote(answer);
      })
      .catch((err) => {
        if (cancelled) return;
        // Ten-minute-old truth on screen beats an error message over it: the
        // refresh failed, the day did not.
        if (!cached) setError(describeError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [day, today, attempt, userId]);

  // «Оновити» on the page empties the day cache; without this the card would
  // keep drawing the day it had and the button would have no visible effect.
  useEffect(() => subscribeDayCache(() => setAttempt((n) => n + 1)), []);

  const [nowMin, setNowMin] = useState(nowMinutes);
  useEffect(() => {
    const timer = window.setInterval(() => setNowMin(nowMinutes()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const dayEvents = isToday ? events : (remote?.events ?? []);
  const dayAvailable = isToday ? available : (remote?.available ?? true);
  // A day that failed to load says so; a day Google refused says Google's note.
  const dayNote = isToday ? note : (remote?.meta.note ?? null);

  const layout = useMemo(
    () => layoutDay(dayEvents, isToday ? nowMin : null),
    [dayEvents, isToday, nowMin],
  );

  const height = ((layout.to - layout.from) / 60) * HOUR_PX;
  const y = (min: number): number => ((min - layout.from) / 60) * HOUR_PX;

  const hours: number[] = [];
  for (let t = layout.from; t < layout.to; t += 60) hours.push(t);

  const nowVisible = isToday && nowMin >= layout.from && nowMin <= layout.to;
  // The «зараз» pill owns the gutter where it stands: two times printed on top
  // of each other read as neither, and the hour it hides is the one hour the
  // pill itself is already saying.
  const hidesHourLabel = (hour: number): boolean =>
    nowVisible && Math.abs(y(hour) - y(nowMin)) < HOUR_PX / 2;

  const scrollRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef(nowMin);
  nowRef.current = nowMin;
  // On a phone the grid is a 320px window onto a taller day, and the useful
  // part of it is where he is now — not 08:00; on another day it is that day's
  // first event. The dependencies are numbers, not the layout object, so the
  // minute tick cannot re-run this and fight his own scrolling. On desktop the
  // container does not scroll and the assignment is a no-op.
  const firstBlockStart = layout.blocks[0]?.start ?? null;
  const gridFrom = layout.from;
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || loading) return;
    const anchor = isToday ? nowRef.current : firstBlockStart;
    if (anchor === null) return;
    box.scrollTop = Math.max(
      0,
      ((anchor - gridFrom) / 60) * HOUR_PX - box.clientHeight / 3,
    );
  }, [day, isToday, loading, gridFrom, firstBlockStart]);

  /* ------------------------------------------------------- the week overlay */

  const cardRef = useRef<HTMLElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  /** The card's geometry at the moment of the press — the panel grows from it. */
  const sourceRect = useRef<DOMRect | null>(null);
  const [expanded, setExpanded] = useState(false);

  // While another day is in flight the grid has nothing to say about it yet:
  // `dayEvents` is still empty, and «день вільний» printed over the shimmer
  // would announce a verdict on a day that has not arrived.
  const emptyMessage = loading
    ? null
    : error
      ? error
      : !dayAvailable
        ? (dayNote ?? "Календар не підключено.")
        : layout.blocks.length === 0 && layout.allDay.length === 0
          ? "Жодної події — день вільний"
          : null;

  return (
    <section
      ref={cardRef}
      className={`jv-card flex flex-col gap-3 p-3 sm:p-4 ${className ?? ""}`}
      aria-label="Мій день"
    >
      {/* What gives when the card is narrow: the date label, and only it. The
          title is two words that wrap the moment they are squeezed, and the
          four chips are icons that cannot shrink at all — so the title and the
          chips hold their size and the 9rem label shrinks under them, down to
          its own ellipsis. Before «На весь екран» joined the row there was
          slack enough for this never to come up; at 1100px there no longer is. */}
      <header className="flex items-center justify-between gap-2">
        <h2 className="shrink-0 text-sm font-semibold text-fg">Мій день</h2>
        <div className="flex min-w-0 items-center gap-0.5">
          <button
            type="button"
            className="jv-btn shrink-0 px-1.5"
            aria-label="Попередній день"
            onClick={() => setPinned(shiftIsoDay(day, -1))}
          >
            <IconChevronLeft className="size-4" />
          </button>
          {/* On today the label is a word, not a control: a button that returns
              you to the day you are already on changes nothing on screen. */}
          {isToday ? (
            <span className="w-36 min-w-0 truncate text-center text-xs text-muted">
              {dayLabel(day, today)}
            </span>
          ) : (
            <button
              type="button"
              className="jv-btn w-36 min-w-0 justify-center"
              title="Повернутись на сьогодні"
              onClick={() => setPinned(null)}
            >
              <span className="min-w-0 truncate">{dayLabel(day, today)}</span>
            </button>
          )}
          <button
            type="button"
            className="jv-btn shrink-0 px-1.5"
            aria-label="Наступний день"
            onClick={() => setPinned(shiftIsoDay(day, 1))}
          >
            <IconChevronRight className="size-4" />
          </button>
          {/* Nothing to open a week of when there is no calendar to read. */}
          {available && (
            <button
              ref={expandRef}
              type="button"
              className="jv-btn ml-1 shrink-0 px-1.5"
              aria-label="На весь екран"
              title="На весь екран"
              onClick={() => {
                sourceRect.current =
                  cardRef.current?.getBoundingClientRect() ?? null;
                setExpanded(true);
              }}
            >
              <IconExpand className="size-4" />
            </button>
          )}
        </div>
      </header>

      {layout.allDay.length > 0 && (
        <ul className="flex flex-wrap items-center gap-1.5">
          {layout.allDay.map((event) => (
            <li key={event.id} className="flex min-w-0">
              <AllDayChip event={event} />
            </li>
          ))}
        </ul>
      )}

      {/* The grid is 14 hours tall and used to print all of them, which made
          this the tallest card in the row and left «Задачі» with 300px of
          nothing beside it (Vitalik, 2026-09-07). It now scrolls on the desktop
          too: `basis-0` keeps those 560px out of the row's intrinsic height, so
          the row is as tall as its other cards and the day fills exactly that,
          down to a floor of 16rem. The effect above already parks the view on
          «зараз». */}
      <div
        ref={scrollRef}
        className="max-h-80 overflow-y-auto lg:max-h-none lg:min-h-64 lg:flex-1 lg:basis-0"
      >
        {/* The top hour label is centred on its own line, so it needs the room
            above it — inside the scroller, so scrolling to the top shows it. */}
        <div className="pt-2 pb-1">
          <div className="relative" style={{ height }}>
            {[...hours, layout.to].map((hour) =>
              hidesHourLabel(hour) ? null : (
                <span
                  key={hour}
                  className="jv-num absolute left-0 w-9 -translate-y-1/2 text-right text-micro text-muted"
                  style={{ top: y(hour) }}
                >
                  {gridHour(hour)}
                </span>
              ),
            )}

            {[...hours, layout.to].map((hour) => (
              <span
                key={`line-${hour}`}
                className="absolute right-0 left-11 border-t border-line"
                style={{ top: y(hour) }}
                aria-hidden="true"
              />
            ))}

            <div className="absolute inset-y-0 right-0 left-11">
              {loading
                ? SKELETON_BLOCKS.map(([top, tall], i) => (
                    <Skeleton
                      key={i}
                      className="absolute inset-x-0"
                      style={{ top: top * HOUR_PX, height: tall * HOUR_PX }}
                    />
                  ))
                : layout.blocks.map((block) => (
                    <EventBlock
                      key={block.event.id}
                      block={block}
                      from={layout.from}
                      hourPx={HOUR_PX}
                    />
                  ))}
            </div>

            {nowVisible && (
              <div
                className="pointer-events-none absolute inset-x-0"
                style={{ top: y(nowMin) }}
                aria-hidden="true"
              >
                <span className="jv-num absolute left-0 -translate-y-1/2 rounded-full bg-accent px-1 text-micro font-medium text-bg">
                  {hm(nowMin)}
                </span>
                <span className="absolute top-0 right-0 left-11 h-px bg-accent" />
              </div>
            )}

            {emptyMessage && (
              <div className="absolute inset-y-0 right-0 left-11 flex flex-col items-center justify-center gap-2 px-2">
                <p
                  className={`max-w-xs text-center text-xs ${error ? "text-danger" : "text-muted"}`}
                  role={error ? "alert" : "status"}
                >
                  {emptyMessage}
                </p>
                {error && (
                  <button
                    type="button"
                    className="jv-btn"
                    onClick={() => setAttempt((n) => n + 1)}
                  >
                    Спробувати ще раз
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <WeekCalendar
          day={day}
          today={today}
          todayEvents={events}
          todayAvailable={available}
          todayNote={note}
          from={sourceRect.current}
          onClose={() => {
            setExpanded(false);
            // Back where the press came from: the overlay took the focus, so
            // it owes it back rather than dropping it on <body>.
            expandRef.current?.focus();
          }}
        />
      )}
    </section>
  );
}

/** Where the shimmer sits while another day is on its way, in hours from the top. */
const SKELETON_BLOCKS: [number, number][] = [
  [1, 1],
  [3.5, 1.5],
  [7, 1],
];
